import { MESSAGE_TYPE_GROUPS } from "features/results/constants/messageTypeGroups";
import { usePatientResults } from "features/results/hooks/usePatientResults";

// Routes the patient Results table through the common results endpoint
// (`/healthlink/common/results?patientId=...`) for every type. Laboratory and
// Radiology are narrowed by the matching `messageTypes` group instead of their
// dedicated endpoints — the common endpoint paginates server-side. The
// isLaboratory/isRadiology flags keep their dedicated columns/detail views.
/*
 * One empty array, not a fresh one per render. `data?.results ?? []` handed out a new array
 * every time the query had no data yet, so anything watching `results` by identity saw a change
 * on every render — which put the results page into an infinite render loop ("Maximum update
 * depth exceeded") the moment one did (KIN-2089's onResultsLoaded).
 *
 * Frozen so the shared instance cannot be mutated by a caller into everyone else's problem.
 */
const NO_RESULTS = Object.freeze([]);

export const usePatientResultsData = ({
  type,
  patientId,
  orderBy,
  messageTypes,
  searchQuery,
  unreviewed,
  limit,
  offset,
}) => {
  const isLaboratory = type === "laboratory";
  const isRadiology = type === "radiology";
  const isCommon = !isLaboratory && !isRadiology;

  const effectiveMessageTypes = isLaboratory
    ? [MESSAGE_TYPE_GROUPS.laboratory]
    : isRadiology
    ? [MESSAGE_TYPE_GROUPS.radiology]
    : messageTypes;

  const { data, isLoading, isError } = usePatientResults({
    patientId,
    orderBy,
    messageTypes: effectiveMessageTypes,
    searchQuery,
    unreviewed,
    limit,
    offset,
  });

  return {
    results: data?.results ?? NO_RESULTS,
    totalCount: data?.totalCount ?? 0,
    currentOffset: data?.currentOffset ?? 0,
    serverPaginated: data?.serverPaginated ?? false,
    isLoading,
    isError,
    isLaboratory,
    isRadiology,
    isCommon,
  };
};
